import { AuditLog } from '../../api/audit-logs/models/api/audit-log';
import { GetAuditLogsResponse } from '../../api/audit-logs/models/api/get-audit-logs-response';
import { ApiListCommentsResponse } from '../../api/core/models';
import {
  ClientParameterComment,
  ClientParameterDetails,
  ClientParameterValueChange,
} from '../../model/client-model/client-parameter';
import { ParameterValueReasonType } from '../../model/enums/parameter-value-reason';

type ReasonAuditLog = Omit<AuditLog, 'comment' | 'oldValue' | 'newValue' | 'traceContext'> & {
  comment: 'EMISSION' | 'ENERGY';
  oldValue: string;
  newValue: string;
  traceContext: string;
};
type ReasonData =
  /**
   * If no reason is found
   */
  | {}
  /**
   * If reason is found on the value auditlog comment field (old handling)
   */
  | { reason: ParameterValueReasonType }
  /**
   * // If there is a dedicated reason auditlog (new handling)
   */
  | { reason: ParameterValueReasonType; reasonOldValue: number; reasonNewValue: number };

const updatedByUsId = '00000000-0000-0000-0000-000000000000';

export function apiAuditLogsAndCommentsToClientParameterDetails(
  logs: GetAuditLogsResponse,
  apiComments: ApiListCommentsResponse,
  variableNameId: string
): ClientParameterDetails {
  // Filter out logs that do not have a reason field in the newValue property.
  // This includes the old handling where "reasonNewValue" is not present.
  const valueAuditLogs = logs.auditLogs?.filter(
    (a) =>
      a.newValue &&
      !JSON.parse(a.newValue).hasOwnProperty('reasonValue') &&
      !JSON.parse(a.newValue).hasOwnProperty('reasonNewValue')
  );

  // Filter logs that have a reason field in the newValue property.
  // This supports both the new handling ("reasonValue") and the old handling ("reasonNewValue").
  const reasonAuditLogs: ReasonAuditLog[] = <ReasonAuditLog[]>(
    logs.auditLogs?.filter(
      (a) =>
        a.newValue &&
        (JSON.parse(a.newValue).hasOwnProperty('reasonValue') ||
          JSON.parse(a.newValue).hasOwnProperty('reasonNewValue'))
    )
  );

  let comments: ClientParameterComment[] = [];
  let valueChanges: ClientParameterValueChange[] = [];

  valueAuditLogs?.forEach((valueAuditLog) => {
    const newValue = valueAuditLog.newValue ? JSON.parse(valueAuditLog.newValue) : {};
    const oldValue = valueAuditLog.oldValue ? JSON.parse(valueAuditLog.oldValue) : {};
    const hasValueChanged =
      newValue.value !== oldValue?.value && newValue.value !== undefined && newValue.value !== null;
    const isUpdatedByUs = valueAuditLog.userId === updatedByUsId;

    const reasonAuditLog = reasonAuditLogs?.find((a) => a.traceContext === valueAuditLog.traceContext);
    let reasonData: ReasonData = {};
    /**
     * In the new way of handling reverse engineered variable values,
     * there is a dedicated reason auditlog containing the reason for the change
     * and the before- and after-values of the reason change.
     */
    if (reasonAuditLog) {
      const { reasonValue: reasonOldValue } = JSON.parse(reasonAuditLog.oldValue);
      const { reasonValue: reasonNewValue } = JSON.parse(reasonAuditLog.newValue);
      reasonData = {
        reason: <ParameterValueReasonType>reasonAuditLog.comment.toLowerCase(),
        reasonOldValue,
        reasonNewValue,
      };
      /**
       * In the old way of handling reverse engineered variable values,
       * the reason for the change was stored in the comment field of the value auditlog,
       * and it did not store the before- and after-values of the reason change.
       */
    } else if (valueAuditLog.comment) {
      reasonData = {
        reason: <ParameterValueReasonType>valueAuditLog.comment.toLowerCase(),
      };
    }

    if (hasValueChanged) {
      valueChanges.push({
        updatedAtMs: valueAuditLog.createdAt ? Date.parse(valueAuditLog.createdAt) : 0,
        updatedBy: isUpdatedByUs ? ' ' : valueAuditLog.userDisplayName || '',
        value: newValue.value,
        ...reasonData,
      });
    }
  });

  apiComments.comments?.forEach((res) => {
    comments.push({
      text: res.comment,
      updatedAtMs: res.createdAt ? Date.parse(res.createdAt) : 0,
      updatedBy: res.displayName ?? '', // in case the user has been deleted from the system
    });
  });

  return {
    comments,
    valueChanges,
    variableNameId,
  };
}
